import * as React from "react";
import {
    ExpandableOptionsContainer,
    Caption,
    Value,
    LeftContainer,
    RightContainer,
    SelectedOptionButton,
    OptionsModal,
} from "./expandable-options-style";
import AppIcon from "common/app-icon";
import ClickOutsideContainer from "sharedUI/ui/smart/click-outside-container/click-outside-container";
import { defined } from "common/Support";

export type ExpandableOptionValue = string | number;

export interface ExpandableOption {
    value: ExpandableOptionValue;
    label: string;
    icon: string;
}

interface Props {
    readonly value: ExpandableOptionValue;
    readonly caption: string;
    readonly onChange: ((newVal: ExpandableOptionValue) => void) | undefined;
    readonly defaultIcon: string;
    readonly options: ExpandableOption[];
    readonly isSvg: boolean;
    readonly isEnabled: boolean;
}
interface State {
    readonly showSelectionModal: boolean;
    selectedValue: ExpandableOptionValue | undefined;
}

// FIXME: Since options is an array, it will always get a new reference from the renderer
// and will render everytime. We should fix this at some point.
export default class ExpandableOptions extends React.Component<Props, State> {
    public state: State = {
        showSelectionModal: false,
        selectedValue: this.props.value,
    };

    public static getDerivedStateFromProps(props: Props, state: State) {
        if (props.value !== state.selectedValue) {
            state.selectedValue = props.value;
        }

        return state;
    }

    private onIconClick = (evt: React.MouseEvent, newValue: ExpandableOptionValue) => {
        evt.stopPropagation();

        const { isEnabled, onChange } = this.props;

        if (!isEnabled || onChange === undefined) return;

        const { selectedValue } = this.state;

        const selectedValueIsCurrent = selectedValue === newValue;
        let newValueOrDefault = newValue;
        if (selectedValueIsCurrent) {
            // Clear the selected value if clicking on a currently selection option
            // Probably a better way to do this and results multple clears if the the user
            // set the same value for multiple reactions
            newValueOrDefault = typeof newValue === "number" ? 0 : "";
        }

        onChange(newValueOrDefault);

        this.setState({
            showSelectionModal: false,
            selectedValue: selectedValueIsCurrent ? undefined : newValue,
        });
    };

    private toggleOptionModal = () => {
        const { showSelectionModal } = this.state;
        const { isEnabled, onChange } = this.props;
        if (!isEnabled || onChange === undefined) return;
        this.setState({ showSelectionModal: !showSelectionModal });
    };

    private hideOptionModal = () => {
        this.setState({ showSelectionModal: false });
    };

    private optionForValue = (value: ExpandableOptionValue | undefined): ExpandableOption | undefined => {
        const { options } = this.props;

        const result = options.find(o => o.value === value);
        return result;
    };

    public render(): React.ReactNode {
        const { caption, options, defaultIcon, isSvg, isEnabled } = this.props;
        const { showSelectionModal, selectedValue } = this.state;

        const selectedOption = this.optionForValue(selectedValue);
        const selectedLabel = selectedOption === undefined ? "-" : selectedOption.label;
        const selectedIcon = selectedOption === undefined ? defaultIcon : selectedOption.icon;

        const iconOptions = options.map(fo => {
            const isSelected = selectedValue !== undefined && selectedValue === fo.value;
            const iconComponent = isSvg ? (
                <AppIcon
                    data-test="app-exp-options-svg"
                    className={isSelected ? "selected icon" : "icon"}
                    icon={defined(fo.icon)}
                    onClick={evt => this.onIconClick(evt, fo.value)}
                />
            ) : (
                <div
                    data-test="app-exp-options-emoji"
                    className={isSelected ? "selected emoji" : "emoji"}
                    onClick={evt => this.onIconClick(evt, fo.value)}>
                    {fo.icon}
                </div>
            );
            return iconComponent;
        });

        const displayIcon = isSvg ? <AppIcon icon={selectedIcon} /> : <div className="emoji">{selectedIcon}</div>;

        const optionPicked = selectedValue !== undefined && selectedValue !== 0 && selectedValue !== "";

        return (
            <ClickOutsideContainer onClickOutside={this.hideOptionModal}>
                <ExpandableOptionsContainer data-test="app-expandable-options" onClick={this.toggleOptionModal}>
                    <LeftContainer>
                        <Caption>{caption}</Caption>
                        <Value>{selectedLabel}</Value>
                    </LeftContainer>
                    <RightContainer selected={optionPicked} disabled={!isEnabled}>
                        <OptionsModal data-test="app-exp-options-open" open={showSelectionModal}>
                            {iconOptions}
                            <div data-test="app-exp-options-close" className="close">
                                <AppIcon icon="00-01-glide-close" color="#959595" size={18} />
                            </div>
                        </OptionsModal>
                        <SelectedOptionButton open={!showSelectionModal}>{displayIcon}</SelectedOptionButton>
                    </RightContainer>
                </ExpandableOptionsContainer>
            </ClickOutsideContainer>
        );
    }
}
